// The Hall: where the town decides who it is. A small deck of customs,
// laws, songs and grievances; a hand of three each dawn, seen only on the
// Hall's phone; a market of ideas; and the COMPASS - two axes the cards
// push the town along, whose quadrants name the government.
//
// Everyone else decides in numbers, space, or a tree; the Hall decides in
// cards. Pure helpers over `civ.hall`.

import { shuffle, nextInt } from './rng/rng';

// ------------------------------------------------------------- the compass
// x: Tradition (-) ... Novelty (+).  y: Order (-) ... Liberty (+).
export const COMPASS_MAX = 5;
export const GOVERNMENT_AT = 2;
export const clampAxis = (v) => Math.max(-COMPASS_MAX, Math.min(COMPASS_MAX, v));

export const GOVERNMENTS = {
  chiefdom: {
    name: 'Chiefdom', where: 'Order + Tradition',
    short: 'Workers turn out even when unhappy. Levies cost no happiness. Fever is 1 in 10 less likely. Festivals give 1 less happiness.',
    text: 'One chief, the old way.',
    mods: { unhappyAt: -1, levyHappiness: 1, fever: -1, festivalJoy: -1 },
  },
  empire: {
    name: 'Empire', where: 'Order + Novelty',
    short: 'Levies give 1 more worker. Units hit on 3 or more. Technologies cost 1 less knowledge. Festivals give 1 less happiness.',
    text: 'Command, with new tools.',
    mods: { levyWorkers: 1, hitOn: -1, techCost: -1, festivalJoy: -1 },
  },
  elders: {
    name: 'Council of Elders', where: 'Liberty + Tradition',
    short: 'Festivals give 1 more happiness. Fever is 2 in 10 less likely. Technologies cost 1 more knowledge.',
    text: 'Talk, and the old rites.',
    mods: { festivalJoy: 1, fever: -2, techCost: 1 },
  },
  assembly: {
    name: 'Assembly', where: 'Liberty + Novelty',
    short: 'Festivals give 1 more happiness. Technologies cost 1 less knowledge. Ponder gives 1 more. Restless Streets is 2 in 10 more likely.',
    text: 'Everyone has a say.',
    mods: { festivalJoy: 1, techCost: -1, ponder: 1, unrest: 2 },
  },
};

// Which corner each government sits in: -1 is Tradition / Order, +1 is
// Novelty / Liberty. A government needs GOVERNMENT_AT on BOTH axes.
export const GOVERNMENT_CORNERS = {
  chiefdom: { x: -1, y: -1 },
  empire: { x: 1, y: -1 },
  elders: { x: -1, y: 1 },
  assembly: { x: 1, y: 1 },
};

/** How many steps on each axis from here to that government's corner. */
export const stepsTo = (civ, id) => {
  const corner = GOVERNMENT_CORNERS[id];
  const { x, y } = compassOf(civ);
  const need = (at, dir) => Math.max(0, GOVERNMENT_AT - at * dir);
  return need(x, corner.x) + need(y, corner.y);
};

export const compassOf = (civ) => civ?.hall?.compass || { x: 0, y: 0 };
/** The government whose square the dot is standing in, if any. */
export const governmentAt = ({ x, y }) => {
  if (Math.abs(x) < GOVERNMENT_AT || Math.abs(y) < GOVERNMENT_AT) return null;
  if (y <= -GOVERNMENT_AT) return x <= -GOVERNMENT_AT ? 'chiefdom' : 'empire';
  return x <= -GOVERNMENT_AT ? 'elders' : 'assembly';
};
// A GOVERNMENT HOLDS until the dot walks into a DIFFERENT square (Matt,
// playtest 9: "we should remain in that government until a different
// government is triggered - not just when the dot leaves its square, but it
// has to enter a different square"). `hall.government` remembers it;
// pushCompass is the only thing that moves the dot, and it keeps this.
export const governmentOf = (civ) => governmentAt(compassOf(civ)) || civ?.hall?.government || null;
export const governmentMods = (civ) => GOVERNMENTS[governmentOf(civ)]?.mods || {};
export const describeCompass = ({ x, y }) => {
  const h = x === 0 ? '' : `${Math.abs(x)} toward ${x < 0 ? 'Tradition' : 'Novelty'}`;
  const v = y === 0 ? '' : `${Math.abs(y)} toward ${y < 0 ? 'Order' : 'Liberty'}`;
  return [h, v].filter(Boolean).join(', ') || 'in the middle';
};

// ------------------------------------------------------------- the cards
// AFTER PLAYTEST 14 (Elizabeth on the Hall, 2026-11-02): a card is BOUGHT
// ONCE and PLAYED FREE. "What's fun is building a little engine and then
// getting to run it, and it didn't really feel like you were getting to run
// your engine" - the goods and coin a play used to cost were what stopped
// it running ("a lot of times I wasn't even playing three cards"). So:
//   price:   what the shelf asks, once (goods, coin, food). The whole cost.
//   effect:  a civ patch, every time it is played. Nothing is paid.
//   tags:    plain words a counselor can LIKE (feast, song, rite, workers,
//            law, grievance, draw). Not kinds - no rules hang off a tag
//            except that one liking; Elizabeth: "I don't know that having
//            the various types of cards is worthwhile... I'd rather they're
//            consistent."
//   with:    at most ONE pair-up ({ tag, text, ...bonus }): played after
//            another card with that tag today, it adds the bonus. The engine
//            the deck-builder is for; the start deck has none.
//   compass: the push; draw: cards; adds: an echo into the EVENT deck;
//   law: permanent mods (the card leaves the deck and stands);
//   grievance: a dead card. Playing it SETTLES it and gives nothing (Matt:
//            "it needs to be an annoyance, but one that is manageable");
//            left in hand at night it festers (-1 happiness); Trash takes
//            it out for a coin.
// Rule for the words on a card: one effect line, at most one "if" line, and
// the "if" is a counselor's liking or a `with`. Never both.
const C = (name, text, extra = {}) => ({
  name, text, effect: {}, compass: {}, tags: [], ...extra,
});

export const HALL_CARDS = {
  // ---- the starting deck (nothing to buy, nothing to pay: the baseline engine)
  rally: C('Rally', 'A speech in the square.', { effect: { happiness: 1 } }),
  feast: C('Feast', 'A feast for the whole town. A card goes into the event deck.', { effect: { happiness: 1 }, compass: { y: 1 }, adds: 'festival_remembered', tags: ['feast'] }),
  levy: C('Levy', 'Extra workers today, for whichever roles need them. The people grumble, and a bigger town grumbles more.', { giveHands: 2, effect: { happiness: -1 }, compass: { y: -1 }, crowd: true, tags: ['workers'] }),
  grow: C('Grow', 'One more person, if there is a House for them. A bigger town grumbles more.', { effect: { population: 1, happiness: -1 }, crowd: true }),
  elders_speak: C('The Elders Speak', 'Draw two cards, and one more buy today.', { draw: 2, buys: 1, tags: ['draw'] }),
  old_custom: C('Old Custom', 'Does nothing unless the town leans to Tradition. A card to trash.', { effect: {}, compass: { x: -1 }, ifTradition: { happiness: 1 }, tags: ['rite'] }),

  // ---- ideas in the air (the market pool, always)
  harvest_rite: C('Harvest Rite', 'A rite at the harvest.', { effect: { food: 4 }, compass: { x: -1 }, price: { goods: 3 }, tags: ['rite'] }),
  census: C('The Census', 'Count the town and write it down.', { effect: { knowledge: 2 }, compass: { x: 1 }, price: { goods: 3 }, with: { tag: 'law', text: 'after a law today: +1 knowledge', knowledge: 1 } }),
  proclamation: C('Proclamation', 'One more worker, wherever it is needed.', { giveHands: 1, compass: { y: -1 }, price: { coin: 2 }, tags: ['workers'] }),
  feast_day: C('Feast Day', 'A day off for the whole town. A card goes into the event deck.', { effect: { happiness: 3 }, compass: { y: 1 }, adds: 'festival_remembered', price: { food: 5 }, tags: ['feast'], with: { tag: 'rite', text: 'after a rite today: +2 food', food: 2 } }),
  tithe: C('Tithe', 'A tenth for the Hall, spent on the people.', { effect: { happiness: 2 }, compass: { y: -1 }, price: { coin: 2 }, tags: ['rite'] }),
  // The Hall helps the Frontier (Brian's ask, playtest 8; Matt 2026-09-28).
  call_to_arms: C('Call to Arms', '+1 worker to the Frontier today (2 actions).', { giveHands: 1, toBand: true, compass: { y: -1 }, price: { goods: 2 }, tags: ['workers'] }),
  gathering: C('The Gathering', 'Everyone in the square. One more buy today.', { buys: 1, effect: { happiness: 1 }, compass: { x: 1 }, price: { food: 1 }, tags: ['draw'] }),

  // ---- unlocked by what the Scholars know. A technology that opens one of
  // these puts it STRAIGHT INTO THE HALL'S DECK, free (playtest 14, Elizabeth:
  // "if Carrie is making discoveries, that should create new cards coming
  // into my pile"); the shelf can still offer another copy.
  law_of_weights: C('Law of Weights', 'One measure in every market. A standing law: markets pay +1.', { compass: { x: 1, y: -1 }, law: { marketCoin: 1 }, price: { coin: 2 }, needsTech: 'writing', tags: ['law'] }),
  law_of_the_well: C('Law of the Well', 'Clean water is everyone\'s. A standing law: fevers are 2 in 10 less likely.', { compass: { y: 1 }, law: { fever: -2 }, price: { coin: 2 }, needsTech: 'writing', tags: ['law'] }),
  law_of_the_watch: C('Law of the Watch', 'Every house sends one to the walls in turn. A standing law: the town\'s people hit on one better when raiders reach the city.', { compass: { y: -1 }, law: { hitOn: -1 }, price: { coin: 2 }, needsTech: 'law', tags: ['law'] }),
  // ONE PER VILLAGE FOUNDED. Matt, 2026-09-20: "I find it hard to believe I
  // would ever choose to get rid of that card" - so a town EATS, which makes
  // playing it a bet rather than an upgrade, and trashing this costs you
  // THAT town rather than expansion itself. "We don't want to make a million
  // towns": the village has to have grown first.
  charter: C('The Charter', 'Name a village that has grown to size 2. It becomes a town: every dawn it sends 1 per size of whatever you order (food, goods or coin), units can muster there, and it eats 1 food a night.', { compass: { x: 1 }, charter: true, tags: ['law'] }),
  song: C('A Song', 'A song everyone sings. A card goes into the event deck.', { effect: { happiness: 2 }, compass: { x: 1, y: 1 }, adds: 'play_remembered', price: { coin: 2 }, needsBranch: 'arts', tags: ['song'], with: { tag: 'song', text: 'after another song today: +1 happiness', happiness: 1 } }),
  play: C('A Play', 'A play in the square. A card goes into the event deck.', { effect: { happiness: 2 }, compass: { y: 1 }, adds: 'play_remembered', price: { goods: 1, coin: 1 }, needsBranch: 'arts', tags: ['song'], with: { tag: 'song', text: 'after another song today: +1 happiness', happiness: 1 } }),
  edict_of_hands: C('Edict of Workers', 'Two workers today, to whoever the Hall names.', { giveHands: 2, effect: { happiness: -1 }, compass: { y: -2 }, price: { coin: 2, goods: 1 }, crowd: true, needsBranch: 'statecraft', tags: ['workers'] }),
  envoy: C('An Envoy', 'Gifts to a neighbouring people. Their temper improves one step.', { envoy: true, compass: { x: 1 }, price: { coin: 2 }, needsRival: true }),
  edict_of_grain: C('Edict of Grain', 'The stores are opened.', { effect: { food: 4 }, compass: { y: -1, x: 1 }, price: { coin: 2 }, needsBranch: 'statecraft', tags: ['rite'] }),

  // ---- BURDENS: the fourth card on the shelf, which PAYS YOU to take it
  // (Nick, playtest 13: "a fourth card that is a bad card that I can take in
  // exchange for some resource"; Matt: "that's a cool idea, make a note").
  // Each is a grievance: `bounty` is what the Hall gets the moment it takes
  // one, and then it is a dead card in the deck until settled (a play) or
  // trashed (a coin). Taking it needs no buy, once a day. Elizabeth found
  // this "really fun"; what was wrong was how it looked (HallBoard).
  loan: C('A Loan', 'Coin borrowed from the merchants. A play settles it; it gives nothing.', { bounty: { coin: 4 }, burden: true, grievance: true, tags: ['grievance'] }),
  owed_labour: C('Owed Labour', 'Goods on credit from the workshops. A play settles it; it gives nothing.', { bounty: { goods: 4 }, burden: true, grievance: true, tags: ['grievance'] }),
  borrowed_grain: C('Borrowed Grain', 'Grain borrowed from a neighbour. A play settles it; it gives nothing.', { bounty: { food: 4 }, burden: true, grievance: true, tags: ['grievance'] }),
  pressed_hands: C('Pressed Hands', 'Workers pressed from the fields for a day. A play settles it; it gives nothing.', { bounty: { knowledge: 2 }, burden: true, grievance: true, tags: ['grievance'] }),

  // ---- what the world puts in the deck
  grievance: C('Grievance', 'The town went hungry and remembers it. A play settles it; it gives nothing.', { grievance: true, tags: ['grievance'] }),
  mourning: C('Mourning', 'Someone died. A play settles it; it gives nothing.', { compass: { x: -1 }, grievance: true, tags: ['grievance'] }),
  grudge: C('Grudge', 'Someone was turned away. A play settles it; it gives nothing.', { grievance: true, tags: ['grievance'] }),
};
// Ten cards, three seats to put them on, and a hand as big as the dawn
// split makes it (dealFor). Matt, when the hand was a fixed five: "if we
// start with three counselors then we need to start with a hand of five
// cards, which means we'll need a larger deck to start (maybe 10 cards,
// draw 5)." Two of the ten are Old Customs on purpose: chaff, and a card
// to trash. And TWO Elders Speak (playtest 6): with one in ten, a five-card
// hand missed it half the rounds and Matt "never found a way to buy a new
// card". With two, some round in four goes without a buy instead of one in two.
export const START_DECK = [
  'rally', 'rally', 'rally', 'feast', 'levy', 'grow', 'elders_speak', 'elders_speak',
  'old_custom', 'old_custom',
];
export const MARKET_SIZE = 3;
// THE HALL'S WORKERS ARE ITS CARDS (Matt, 2026-10-14: "each worker that
// you're given at dawn gives you two cards at the start of your turn", and
// "cards would not cost a worker"). The hand is dealt when the day starts,
// CARDS_PER_WORKER for every worker the dawn split gave the Hall, and the
// council is the limit on plays: one card to each counselor. So the Hall's
// dawn ask is for card draw, and a card is paid for in goods and coin.
export const CARDS_PER_WORKER = 2;
/** Cards the Hall is dealt at the start of a day, for the workers it was given (never below zero). */
export const dealFor = (workers, drawLess = 0) => Math.max(0, (workers || 0) * CARDS_PER_WORKER - (drawLess || 0));

/** Ideas the market may show: always-available ones plus what is unlocked. */
export const BURDENS = Object.keys(HALL_CARDS).filter((id) => HALL_CARDS[id].burden);
export const poolFor = (civ) => Object.entries(HALL_CARDS)
  .filter(([id, c]) => c.price && !c.burden && !START_DECK.includes(id))
  .filter(([, c]) => !c.needsTech || (civ.techs || []).includes(c.needsTech))
  .filter(([, c]) => !c.needsBranch || (civ.branches || []).includes(c.needsBranch))
  .filter(([, c]) => !c.needsRival || (civ.rivals || []).some((r) => r.met))
  .map(([id]) => id);

export const startHall = (rng) => {
  let deck;
  [deck, rng] = shuffle(START_DECK, rng);
  // The founders' terms are dealt at the end of createGameState (after
  // everything else the seed decides), so the draws that came before
  // terms existed still land where they did.
  const council = [{ id: 'elder', since: 1 }, { id: 'crier', since: 1 }, { id: 'reeve', since: 1 }];
  const hall = {
    deck,
    hand: [],
    discard: [],
    market: [],
    laws: [],
    // BUYING IS NOT FREE ANY MORE. Matt, 2026-09-20: "I don't think you
    // should get to buy a card every round as the hall. I think there
    // should be a card or two in the deck that enable you to buy a card."
    // The Elders Speak (in the starting ten) and The Gathering (on the
    // shelf) each grant one, for the day they are played.
    buys: 1, // HALL_BUYS_PER_DAY - one idea a day from the first round
    compass: { x: 0, y: 0 },
    // Three from the first round: the council IS the Hall's board now, and
    // a board with one space on it is not a puzzle.
    council,
    waiting: [],
    offer: [],
    offerBy: {},
    burden: null,
    left: {},
    // Which seats have taken a card today, and which cards were played
    // (for the pair-ups). Both cleared every night.
    used: {},
    played: [],
  };
  return [hall, rng];
};

export const hallOf = (civ) => civ?.hall || null;

/** Draw n cards, reshuffling the discard under the deck when it runs dry. Returns [hall, rng]. */
export const draw = (hall, n, rng) => {
  let { deck, discard, hand } = hall;
  deck = deck.slice(); discard = discard.slice(); hand = hand.slice();
  for (let i = 0; i < n; i += 1) {
    if (!deck.length) {
      if (!discard.length) break;
      [deck, rng] = shuffle(discard, rng);
      discard = [];
    }
    hand.push(deck.shift());
  }
  return [{ ...hall, deck, discard, hand }, rng];
};

/** The ideas a counselor brings to the shelf that the town can take up now, not already on it. */
const ownIdeas = (civ, id, onShelf) => (COUNSELORS[id]?.offers || []).filter((c) => poolFor(civ).includes(c) && !onShelf.has(c));

/**
 * THE SHELF IS THE COUNCIL'S (playtest 13). Three chairs, three cards: each
 * seated counselor puts ONE idea of their own on the shelf (`offers`, on the
 * counselor; `hall.offerBy` says whose is whose). Nick: "it would be cool if
 * each card in the offer was offered by one of the council, one to one"; Matt:
 * "there's a nice correlation there". A counselor who leaves takes their card
 * with them, a new one brings theirs, a bought card is replaced by another of
 * that counselor's, and every night one counselor swaps theirs (rotateShelf),
 * so the shelf turns over at a pace without ever resetting at once. A
 * counselor with nothing of their own to offer brings anything in the air.
 * Returns [hall, rng].
 */
export const refillMarket = (hall, civ, rng) => {
  const council = (hall.council || []).map((c) => c.id);
  const market = (hall.market || []).slice();
  const onShelf = new Set(market);
  // Whose card is whose: drop anyone unseated and any card no longer there.
  const offerBy = {};
  Object.entries(hall.offerBy || {}).forEach(([who, card]) => { if (council.includes(who) && onShelf.has(card)) offerBy[who] = card; });
  // A card on the shelf nobody is credited with (an old save, a test) goes
  // to a seated counselor without one - theirs by taste if it is.
  market.filter((card) => !Object.values(offerBy).includes(card)).forEach((card) => {
    const who = council.find((id) => !offerBy[id] && (COUNSELORS[id]?.offers || []).includes(card)) || council.find((id) => !offerBy[id]);
    if (who) offerBy[who] = card;
  });
  // Everyone seated puts one up.
  for (const id of council) {
    if (offerBy[id]) continue;
    let from = ownIdeas(civ, id, onShelf);
    if (!from.length) from = poolFor(civ).filter((c) => !onShelf.has(c));
    if (!from.length) continue;
    let at;
    [at, rng] = nextInt(from.length, rng);
    offerBy[id] = from[at];
    onShelf.add(from[at]);
    market.push(from[at]);
  }
  // A card credited to nobody (the chair emptied) comes down.
  const kept = market.filter((card) => Object.values(offerBy).includes(card)).slice(0, Math.max(MARKET_SIZE, council.length));
  return [{ ...hall, market: kept, offerBy }, rng];
};

/**
 * One counselor a night puts a different idea of theirs up (round-robin by
 * the round, so each chair turns over every few months - Matt: "cards
 * changing round to round at some pace, although not necessarily every
 * round"). Returns [hall, rng, line].
 */
export const rotateShelf = (hall, civ, round, rng) => {
  const council = (hall.council || []).map((c) => c.id);
  if (!council.length) return [hall, rng, null];
  const who = council[round % council.length];
  const current = (hall.offerBy || {})[who];
  const onShelf = new Set(hall.market || []);
  const from = ownIdeas(civ, who, onShelf);
  if (!from.length) return [hall, rng, null];
  let at;
  [at, rng] = nextInt(from.length, rng);
  const next = from[at];
  const market = (hall.market || []).filter((c) => c !== current).concat(next);
  const offerBy = { ...(hall.offerBy || {}), [who]: next };
  const line = `${COUNSELORS[who].name} puts a new idea on the shelf: ${HALL_CARDS[next].name}${current ? ` (${HALL_CARDS[current].name} is put away)` : ''}`;
  return [{ ...hall, market, offerBy }, rng, line];
};

/** Who put a shelf card up (null for an old save's card nobody is credited with). */
export const offeredBy = (hall, card) => Object.entries(hall?.offerBy || {}).find(([, c]) => c === card)?.[0] || null;

/** The burden slot: a card that pays you to take it. Deal one if the slot is empty. Returns [hall, rng]. */
export const dealBurden = (hall, rng) => {
  if (hall.burden || !BURDENS.length) return [hall, rng];
  let at;
  [at, rng] = nextInt(BURDENS.length, rng);
  return [{ ...hall, burden: BURDENS[at] }, rng];
};

/** A card the world adds to the Hall's deck (it comes around on the next shuffle). */
export const grieve = (civ, id) => {
  const hall = hallOf(civ);
  if (!hall || !HALL_CARDS[id]) return civ;
  return { ...civ, hall: { ...hall, discard: [...hall.discard, id] } };
};

/**
 * Take one Charter back out of the Hall's papers, wherever it is sitting.
 * A village that burns down takes its own charter with it - there is
 * nothing left to charter - and a mourning goes in instead.
 */
export const dropCharter = (civ) => {
  const hall = hallOf(civ);
  if (!hall) return civ;
  for (const pile of ['hand', 'deck', 'discard']) {
    const i = (hall[pile] || []).indexOf('charter');
    if (i >= 0) {
      const next = hall[pile].slice();
      next.splice(i, 1);
      return { ...civ, hall: { ...hall, [pile]: next } };
    }
  }
  return civ;
};

export const pushCompass = (civ, delta = {}) => {
  const hall = hallOf(civ);
  if (!hall) return civ;
  const c = compassOf(civ);
  const compass = { x: clampAxis(c.x + (delta.x || 0)), y: clampAxis(c.y + (delta.y || 0)) };
  const government = governmentAt(compass) || governmentOf(civ);
  return { ...civ, hall: { ...hall, compass, government } };
};

// KEEPSAKES (DESIGN.md "The Year", 2026-09-27). A counselor who served
// KEEPSAKE_AFTER months or more leaves a card in the Hall's deck when they
// retire: a faint version of their twist, in their name. The deck slowly
// fills with the town's own history - which is the deck-building Matt
// asked to lean into - and Trash still lets go of one the Hall does not
// want. Never on the shelf (no price); `remember` puts one in the discard.
// Free to play: a keepsake is a memory, not an errand.
const KS = (name, text, extra) => C(name, text, { keepsake: true, ...extra });
export const KEEPSAKES = {
  keepsake_elder: KS('The Elder\'s Counsel', '+1 card, +1 Tradition.', { draw: 1, compass: { x: -1 } }),
  keepsake_crier: KS('The Crier\'s Call', '+1 happiness, +1 Liberty.', { effect: { happiness: 1 }, compass: { y: 1 } }),
  keepsake_reeve: KS('The Reeve\'s Tally', '+1 worker for a role you name.', { giveHands: 1 }),
  keepsake_miller: KS('The Mill\'s Due', '+1 worker for a role you name.', { giveHands: 1 }),
  keepsake_potter: KS('The Potter\'s Jar', '+2 food.', { effect: { food: 2 } }),
  keepsake_smith: KS('The Smith\'s Hammer', '+1 goods, +1 Novelty.', { effect: { goods: 1 }, compass: { x: 1 } }),
  keepsake_drover: KS('The Drover\'s Road', '+1 card, +1 Liberty.', { draw: 1, compass: { y: 1 } }),
  keepsake_priest: KS('The Old Rite', '+1 happiness, +1 Tradition.', { effect: { happiness: 1 }, compass: { x: -1 } }),
  keepsake_bard: KS('The Bard\'s Song', '+2 happiness.', { effect: { happiness: 2 } }),
  keepsake_captain: KS('The Captain\'s Drill', '+1 order to the Frontier.', { giveHands: 1, toBand: true }),
  keepsake_merchant: KS('The Merchant\'s Ledger', '+2 coin.', { effect: { coin: 2 } }),
  keepsake_scholar: KS('The Scholar\'s Notes', '+2 knowledge.', { effect: { knowledge: 2 } }),
  keepsake_stranger: KS('The Stranger\'s Way', '+1 card, +1 Novelty, +1 Liberty.', { draw: 1, compass: { x: 1, y: 1 } }),
  keepsake_mason: KS('The Mason\'s Mark', 'The next stage of the great work costs 1 less goods.', { stone: true }),
  keepsake_widow: KS('The Widow\'s Watch', '+1 happiness, +1 card.', { effect: { happiness: 1 }, draw: 1 }),
  keepsake_chieftain: KS('The Chieftain\'s Word', '+2 workers for a role you name, +1 Order.', { giveHands: 2, compass: { y: -1 } }),
  keepsake_general: KS('The General\'s Map', '+1 order to the Frontier, +1 Novelty.', { giveHands: 1, toBand: true, compass: { x: 1 } }),
  keepsake_matriarch: KS('The Matriarch\'s Table', '+1 happiness, +1 card.', { effect: { happiness: 1 }, draw: 1 }),
  keepsake_speaker: KS('The Speaker\'s Turn', '+1 knowledge, +1 card, +1 Liberty.', { effect: { knowledge: 1 }, draw: 1, compass: { y: 1 } }),
};
Object.assign(HALL_CARDS, KEEPSAKES);
export const keepsakeOf = (counselorId) => (KEEPSAKES[`keepsake_${counselorId}`] ? `keepsake_${counselorId}` : null);
/** A retired counselor is remembered: their keepsake goes into the Hall's discard. */
export const remember = (civ, counselorId) => {
  const id = keepsakeOf(counselorId);
  const hall = hallOf(civ);
  if (!id || !hall) return civ;
  return { ...civ, hall: { ...hall, discard: [...hall.discard, id] } };
};

/** Standing laws' mods, summed. */
export const lawMods = (civ) => (hallOf(civ)?.laws || []).reduce((acc, id) => {
  Object.entries(HALL_CARDS[id]?.law || {}).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; });
  return acc;
}, {});

export const cardPrice = (id) => HALL_CARDS[id]?.price || {};

// ------------------------------------------------------------- the council
// Counselors are not cards. They EMERGE from what happens - the town's
// lean, a branch opened, a camp cleared, a road found - and sit for a
// term. Each takes certain kinds of card and reinterprets them: the same
// Feast is a rite through the Priest and a fair through the Merchant.
// Matt: "the counselors represent options for the Hall player for ways to
// use the cards they've drawn."

export const COUNCIL_SEATS = 3;
// After this many rounds in the chair a counselor is OLD: still taken,
// but their pull on the compass is gone. (Matt: "their impact on the
// compass lessened as they're there longer.")
export const OLD_AFTER = 4;
export const RETURN_AFTER = 4;
// COUNSELORS WEAR OUT ON A RANDOMISED TIMER (Matt, playtest 6: "let's have
// them just wear out on a randomized timer"). Every counselor is dealt a
// TERM when they take the chair - this many rounds, drawn from the range -
// and retires at night when it is up. The range is what keeps the founding
// three from leaving together. When a chair empties the Hall is OFFERED
// two candidates, drawn from whoever's moment has come and weighted by
// where the compass sits, and picks one at dawn. Nobody waits at the door
// any more.
export const TERM = { min: 4, max: 8 };
export const OFFER_SIZE = 2;
export const drawTerm = (rng) => {
  let n;
  [n, rng] = nextInt(TERM.max - TERM.min + 1, rng);
  return [TERM.min + n, rng];
};

// A counselor is a SEAT on the Hall's board. Every card you play goes onto
// one of them, and every seat takes ONE card a day: a hand as big as the
// dawn split bought (two cards a worker), three seats, and nothing to pay.
//
// EACH COUNSELOR LIKES ONE THING, AND SAYS SO (playtest 14). Elizabeth's one
// fun moment on the Hall was finding out the Widow liked grievances; what
// confused her was everything else - "the same card gives different
// happiness and I have no idea why". So a counselor has exactly one
// `likes`: a tag, the words for the chair, and the one bonus it adds when a
// card with that tag is played to them. It is printed on the chair before
// you tap, and the chair lights when you pick up a card they like. No other
// twist, no hidden reading. (The old `takes`/`via` pairs - several kinds,
// several effects each - are gone; face-down plays went in playtest 7.)
//
// A bonus is ONE of: a patch (happiness/knowledge/food/coin), draw, hands
// (+workers; toBand sends every worker the card raises to the Frontier),
// reveal (a Scholars' aspect), stone (the great work), compass.
const K = (name, job, lean, emerges, likes, text, extra = {}) => ({
  name, job, lean, emerges, likes, text, ...extra,
});
const L = (tag, text, bonus) => ({ tag, text, ...bonus });
/** The words for a tag, as a chair says them: "likes feasts". */
export const TAG_WORDS = {
  feast: 'feasts', song: 'songs', rite: 'the old rites', workers: 'cards that raise workers', law: 'laws', grievance: 'grievances', draw: 'cards that draw',
};
export const tagWords = (tag) => TAG_WORDS[tag] || tag;

export const COUNSELORS = {
  // ---- the three who are there from the first round
  elder: K('The Elder', 'keeper of the old ways', { x: -1 }, 'with you from the start',
    L('rite', 'likes the old rites: +1 card', { draw: 1 }),
    'Remembers how it was done.',
    { when: () => true, offers: ['harvest_rite', 'feast_day', 'tithe', 'census'] }),
  crier: K('The Crier', 'voice in the square', { y: 1 }, 'with you from the start',
    L('workers', 'likes cards that raise workers: +1 happiness', { happiness: 1 }),
    'If it is not said out loud it did not happen.',
    { when: () => true, offers: ['proclamation', 'gathering', 'song', 'play', 'tithe'] }),
  reeve: K('The Reeve', 'the Hall\'s worker', { y: -1 }, 'with you from the start',
    L('workers', 'likes cards that raise workers: +1 worker', { hands: 1 }),
    'Somebody has to count.',
    { when: () => true, offers: ['proclamation', 'edict_of_hands', 'edict_of_grain', 'census', 'call_to_arms'] }),

  // ---- the commoners: nobody's moment, anybody's chair. They never emerge
  // on their own; they STAND AT A MOOT when a chair is empty and nobody has
  // come forward (playtest 7: the council ran dry). See drawMoot.
  miller: K('The Miller', 'grinds for everyone', { y: -1 }, 'stands at a moot',
    L('feast', 'likes feasts: +2 food', { food: 2 }),
    'Everyone eats; everyone owes the mill.',
    { when: () => false, common: true, offers: ['harvest_rite', 'edict_of_grain', 'proclamation'] }),
  potter: K('The Potter', 'whose jars keep the winter', { x: -1 }, 'stands at a moot',
    L('grievance', 'likes grievances: +1 card', { draw: 1 }),
    'Cracked pots still hold something.',
    { when: () => false, common: true, offers: ['census', 'tithe', 'harvest_rite'] }),
  smith: K('The Smith', 'hammer in the square', { x: 1 }, 'stands at a moot',
    L('workers', 'likes cards that raise workers: +1 goods', { goods: 1 }),
    'If it can be made, it can be made better.',
    { when: () => false, common: true, offers: ['edict_of_hands', 'call_to_arms', 'census'] }),
  drover: K('The Drover', 'in from the road', { y: 1 }, 'stands at a moot',
    L('song', 'likes songs: +1 card', { draw: 1 }),
    'Has seen how other towns do it.',
    { when: () => false, common: true, offers: ['song', 'play', 'envoy', 'gathering'] }),

  // ---- and the ones the world produces
  priest: K('The Priest', 'voice of the rites', { x: -1, y: -1 }, 'when the town leans to Tradition',
    L('feast', 'likes feasts: +1 happiness', { happiness: 1 }),
    'There is a rite for everything, including this.',
    { when: (civ) => (civ.hall?.compass?.x ?? 0) <= -2, ask: 'the Hall: lean the town to Tradition (-2)', askRole: 'culture', offers: ['harvest_rite', 'feast_day', 'tithe', 'law_of_the_well'] }),
  bard: K('The Bard', 'singer in the hall', { y: 1 }, 'when the Arts open',
    L('song', 'likes songs: +2 happiness', { happiness: 2 }),
    'Every town needs someone to sing about it.',
    { when: (civ) => (civ.branches || []).includes('arts'), ask: 'the Hall: a town at 8 happiness opens the Arts', askRole: 'culture', offers: ['song', 'play', 'gathering', 'feast_day'] }),
  captain: K('The Captain', 'of the warbands', { y: -1 }, 'when a camp is first cleared',
    L('workers', 'likes cards that raise workers: they all go to the Frontier (2 actions each)', { toBand: true }),
    'Give me the people and I will give you the walls.',
    { when: (civ) => (civ.cleared || 0) > 0, ask: 'the Frontier: clear a camp', askRole: 'military', offers: ['call_to_arms', 'edict_of_hands', 'law_of_the_watch', 'proclamation'] }),
  merchant: K('The Merchant', 'of the market', { x: 1 }, 'when the Old Road is found, or the second market built',
    L('rite', 'likes the old rites: +2 coin', { coin: 2 }),
    'Everything has a price, and I know it.',
    { when: (civ) => (civ.regions || []).includes('old_road') || (civ.markets || 0) >= 2, ask: 'the Frontier: find the Old Road, or the Stewards: a second market', askRole: 'trade', offers: ['census', 'tithe', 'envoy', 'law_of_weights', 'edict_of_grain'] }),
  scholar: K('The Scholar', 'reader of the stones', { x: 1 }, 'with Writing',
    L('law', 'likes laws: reads out one principle\'s nature to the Scholars', { reveal: true }),
    'It is all written down somewhere.',
    { when: (civ) => (civ.techs || []).includes('writing'), ask: 'the Scholars: learn Writing', askRole: 'science', offers: ['census', 'law_of_weights', 'law_of_the_well', 'law_of_the_watch'] }),
  stranger: K('The Stranger', 'who stayed', { x: 1, y: 1 }, 'if the wanderers were taken in',
    L('feast', 'likes feasts: +1 knowledge', { knowledge: 1 }),
    'Where I come from, we do it differently.',
    { when: (civ) => Boolean(civ.strangerStayed), ask: 'the table: take the wanderers in when they come', askRole: null, offers: ['envoy', 'play', 'gathering', 'song'] }),
  mason: K('The Mason', 'of the great work', {}, 'when a goal to build is chosen',
    L('rite', 'likes the old rites: the next stage of the great work costs 1 less goods', { stone: true }),
    'Every stone I set outlives me.',
    { when: (civ) => Boolean(civ.monument?.revealed), ask: 'the table: choose a great work', askRole: null, offers: ['census', 'proclamation', 'tithe'] }),
  widow: K('The Widow', 'who buried them', { x: -1 }, 'after a famine or a death',
    L('grievance', 'likes grievances: +1 happiness', { happiness: 1 }),
    'I know what it costs. Let me carry it.',
    { when: (civ) => Boolean(civ.grieved), ask: 'after a famine or a death - nobody asks for this', askRole: null, offers: ['tithe', 'feast_day', 'harvest_rite'] }),
  chieftain: K('The Chieftain', 'of the Chiefdom', { x: -1, y: -1 }, 'when the town sits as a Chiefdom',
    L('workers', 'likes cards that raise workers: +2 workers', { hands: 2 }),
    'One voice. Mine.',
    { when: (civ) => governmentOf(civ) === 'chiefdom', ask: 'the Hall: sit as a Chiefdom (Tradition and Order)', askRole: 'culture', offers: ['edict_of_hands', 'call_to_arms', 'proclamation', 'harvest_rite'] }),
  general: K('The General', 'of the Empire', { x: 1, y: -1 }, 'when the town sits as an Empire',
    L('workers', 'likes cards that raise workers: +1 worker, and they all go to the Frontier', { hands: 1, toBand: true }),
    'The border is wherever I say it is.',
    { when: (civ) => governmentOf(civ) === 'empire', ask: 'the Hall: sit as an Empire (Novelty and Order)', askRole: 'culture', offers: ['call_to_arms', 'edict_of_hands', 'law_of_the_watch', 'envoy'] }),
  matriarch: K('The Matriarch', 'of the Elders', { x: -1, y: 1 }, 'when the town sits as a Council of Elders',
    L('feast', 'likes feasts: +2 happiness', { happiness: 2 }),
    'Sit. Eat. Then we talk.',
    { when: (civ) => governmentOf(civ) === 'elders', ask: 'the Hall: sit as a Council of Elders (Tradition and Liberty)', askRole: 'culture', offers: ['feast_day', 'harvest_rite', 'gathering', 'tithe'] }),
  speaker: K('The Speaker', 'of the Assembly', { x: 1, y: 1 }, 'when the town sits as an Assembly',
    L('law', 'likes laws: +2 knowledge', { knowledge: 2 }),
    'Everyone gets a turn. Even you.',
    { when: (civ) => governmentOf(civ) === 'assembly', ask: 'the Hall: sit as an Assembly (Novelty and Liberty)', askRole: 'culture', offers: ['law_of_weights', 'law_of_the_well', 'gathering', 'census', 'song'] }),
};
export const COUNSELOR_ORDER = Object.keys(COUNSELORS);

// The three who are there from the first round. A game dealt before the
// council became the board started with only the Elder, so any save can be
// brought up to date: seat a founder who is missing, has never been seated,
// and has never been shown the door. A dismissal is never undone.
export const FOUNDERS = ['elder', 'crier', 'reeve'];

export const councilOf = (civ) => civ?.hall?.council || [];
export const waitingOf = (civ) => civ?.hall?.waiting || [];
/** The candidates offered for an empty chair, chosen at dawn. */
export const offerOf = (civ) => civ?.hall?.offer || [];
/** Rounds a counselor has left in the chair (null for a save from before terms). */
export const termLeft = (civ, id, round) => {
  const c = councilOf(civ).find((x) => x.id === id);
  if (!c || !c.term) return null;
  return Math.max(0, c.term - (round - c.since));
};
/** Ideas the Hall may take up today, granted by the cards it has played. */
export const buysOf = (civ) => civ?.hall?.buys || 0;
export const seated = (civ, id) => councilOf(civ).some((c) => c.id === id);
/** Old: in the chair OLD_AFTER rounds or more. Still taken; no pull on the compass. */
export const isOld = (civ, id, round) => {
  const c = councilOf(civ).find((x) => x.id === id);
  return Boolean(c) && round - c.since >= OLD_AFTER;
};
/** Does this counselor like this card? (The chair lights up; the bonus rides on the play.) */
export const likesCard = (counselorId, cardId) => {
  const tag = COUNSELORS[counselorId]?.likes?.tag;
  return Boolean(tag) && (HALL_CARDS[cardId]?.tags || []).includes(tag);
};
/** Counselors on the council who like this card. */
export const counselorsFor = (civ, cardId) => councilOf(civ)
  .map((c) => c.id)
  .filter((id) => likesCard(id, cardId));

/**
 * ANY seated counselor will take a card; only one who LIKES it adds their
 * bonus. That is what keeps the board from dead-ending: you can always put a
 * card somewhere, but putting it where it is liked is the game. Returns the
 * counselor id when the liking applies, else null.
 */
export const likingFor = (civ, cardId, counselor) => (
  counselor && seated(civ, counselor) && likesCard(counselor, cardId) ? counselor : null
);

/** A card's pair-up, if a card with its `with` tag was already played today. */
export const pairUpFor = (civ, cardId) => {
  const w = HALL_CARDS[cardId]?.with;
  if (!w) return null;
  const played = hallOf(civ)?.played || [];
  return played.some((id) => (HALL_CARDS[id]?.tags || []).includes(w.tag)) ? w : null;
};

/**
 * What the Hall has left in a day, for the role tabs and the table: its
 * workers were dealt into cards when the day started, so "left" is cards in
 * hand and counselors who have not taken one. `short` is the tab's count.
 */
export const hallLeft = (civ) => {
  const hand = hallOf(civ)?.hand?.length || 0;
  const seats = councilOf(civ).filter((c) => !civ?.hall?.used?.[c.id]).length;
  return { hand, seats, playable: Math.min(hand, seats) };
};
export const hallLeftWords = (civ) => {
  const { hand, seats } = hallLeft(civ);
  return `${hand} ${hand === 1 ? 'card' : 'cards'}, ${seats} ${seats === 1 ? 'seat' : 'seats'} left`;
};

/** Has this seat already taken a card today? */
export const seatUsed = (civ, id) => Boolean(civ?.hall?.used?.[id]);
export const freeSeats = (civ) => councilOf(civ).filter((c) => !seatUsed(civ, c.id));

/**
 * Whose moment has come: not seated, not waiting, not already offered,
 * their `when` holds, and either never served or gone long enough to come
 * back. This is the pool an OFFER is drawn from.
 */
export const emerging = (civ, round) => {
  const council = councilOf(civ);
  const waiting = waitingOf(civ);
  const offer = offerOf(civ);
  const left = civ.hall?.left || {};
  return COUNSELOR_ORDER.filter((id) => {
    if (council.some((c) => c.id === id) || waiting.includes(id) || offer.includes(id)) return false;
    if (!COUNSELORS[id].when || !COUNSELORS[id].when(civ, round)) return false;
    return left[id] === undefined || round - left[id] >= RETURN_AFTER;
  });
};

/**
 * THE POOL (playtest 13, Matt: "a whole grid of cards that represent people
 * who could possibly [sit]... the person in the hall could see what
 * candidates are possible right now, but could also see some disabled ones
 * that they could ask for some help [with]"). Everyone not in a chair, with
 * where they stand: `offered` (put forward now), `ready` (could be drawn for
 * the next empty chair), `resting` (left recently; back in `back` rounds),
 * `locked` (their moment has not come: `ask` says who could bring it), or
 * `moot` (a commoner: stands only when nobody else will).
 */
export const candidatePool = (civ, round) => {
  const council = councilOf(civ).map((c) => c.id);
  const offer = offerOf(civ);
  const left = civ?.hall?.left || {};
  return COUNSELOR_ORDER.filter((id) => !council.includes(id)).map((id) => {
    const c = COUNSELORS[id];
    const rested = left[id] === undefined ? 0 : Math.max(0, RETURN_AFTER - (round - left[id]));
    let status = 'ready';
    if (offer.includes(id)) status = 'offered';
    else if (c.common) status = 'moot';
    else if (!c.when(civ, round)) status = 'locked';
    else if (rested > 0) status = 'resting';
    return { id, name: c.name, job: c.job, status, back: rested, emerges: c.emerges, ask: c.ask || null, askRole: c.askRole || null, likes: c.likes?.text || '' };
  });
};

/** Counselors whose term is up tonight. */
export const retiring = (civ, round) => councilOf(civ).filter((c) => c.term && round - c.since >= c.term);

/** A counselor's term is up: the chair empties, and they may return later like anyone dismissed. */
export const retire = (civ, id, round) => {
  const hall = civ.hall;
  if (!seated(civ, id)) return civ;
  return { ...civ, hall: { ...hall, council: councilOf(civ).filter((c) => c.id !== id), left: { ...(hall.left || {}), [id]: round } } };
};

/** A save from before terms: deal one to anybody sitting without one. Returns [civ, rng]. */
export const dealMissingTerms = (civ, rng) => {
  if (!civ?.hall || councilOf(civ).every((c) => c.term)) return [civ, rng];
  const council = [];
  for (const c of councilOf(civ)) {
    if (c.term) { council.push(c); continue; }
    let term;
    [term, rng] = drawTerm(rng);
    council.push({ ...c, term });
  }
  return [{ ...civ, hall: { ...civ.hall, council } }, rng];
};

/**
 * How much the compass favours a candidate: one for each axis where their
 * lean points the way the town already sits. The weight of the draw.
 */
export const leanWeight = (civ, id) => {
  const lean = COUNSELORS[id]?.lean || {};
  const { x, y } = compassOf(civ);
  let w = 1;
  if (lean.x && x && Math.sign(lean.x) === Math.sign(x)) w += 1;
  if (lean.y && y && Math.sign(lean.y) === Math.sign(y)) w += 1;
  return w;
};

/** Draw up to OFFER_SIZE candidates for an empty chair, weighted by the compass. Returns [ids, rng]. */
export const drawOffer = (civ, round, rng) => {
  let pool = emerging(civ, round);
  const out = [];
  while (out.length < OFFER_SIZE && pool.length) {
    const weights = pool.map((id) => leanWeight(civ, id));
    const total = weights.reduce((a, b) => a + b, 0);
    let roll;
    [roll, rng] = nextInt(total, rng);
    let i = 0;
    while (roll >= weights[i]) { roll -= weights[i]; i += 1; }
    out.push(pool[i]);
    pool = pool.filter((id) => id !== pool[i]);
  }
  return [out, rng];
};

export const COMMONERS = Object.keys(COUNSELORS).filter((id) => COUNSELORS[id].common);

/**
 * A moot: two commoners stand for an empty chair nobody emerged for.
 * Weighted by the compass like any offer. Returns [ids, rng].
 */
export const drawMoot = (civ, rng) => {
  let pool = COMMONERS.filter((id) => !seated(civ, id) && !waitingOf(civ).includes(id));
  const out = [];
  while (out.length < OFFER_SIZE && pool.length) {
    const weights = pool.map((id) => leanWeight(civ, id));
    const total = weights.reduce((a, b) => a + b, 0);
    let roll;
    [roll, rng] = nextInt(total, rng);
    let i = 0;
    while (roll >= weights[i]) { roll -= weights[i]; i += 1; }
    out.push(pool[i]);
    pool = pool.filter((id) => id !== pool[i]);
  }
  return [out, rng];
};

/**
 * The Hall gives an empty chair to one of the offered. Returns
 * [civ, rng, line]. Emerged candidates are chosen outright; at a MOOT the
 * Hall only BACKS one - a die of MOOT_WIN or more seats them, anything
 * less seats the other (Matt, playtest 7: "fun but wouldn't allow them to
 * pick the councilor with certainty").
 */
export const chooseCounselor = (civ, id, round, rng, mootWin = 4) => {
  const hall = civ.hall;
  const offer = offerOf(civ);
  if (!offer.includes(id) || councilOf(civ).length >= COUNCIL_SEATS) return [civ, rng, null];
  let seat = id;
  let line = null;
  if (hall.moot && offer.length > 1) {
    let roll;
    [roll, rng] = nextInt(6, rng);
    roll += 1;
    const other = offer.find((x) => x !== id);
    if (roll >= mootWin) {
      line = `the moot backs ${COUNSELORS[id].name}; the square rolls a ${roll} - ${COUNSELORS[id].name} takes the chair`;
    } else {
      seat = other;
      line = `the moot backs ${COUNSELORS[id].name}; the square rolls a ${roll} - ${COUNSELORS[other].name} takes the chair instead`;
    }
  }
  let term;
  [term, rng] = drawTerm(rng);
  return [{ ...civ, hall: { ...hall, offer: [], moot: false, council: [...councilOf(civ), { id: seat, since: round, term }] } }, rng, line];
};

/** A counselor arrives: a free chair seats them; a full council leaves them at the door. Returns [civ, lines]. */
export const arrive = (civ, id, round) => {
  const hall = civ.hall;
  if (councilOf(civ).length < COUNCIL_SEATS) {
    return [{ ...civ, hall: { ...hall, council: [...councilOf(civ), { id, since: round }] } }, [`${COUNSELORS[id].name}, ${COUNSELORS[id].job}, takes a chair in the Hall`]];
  }
  return [{ ...civ, hall: { ...hall, waiting: [...waitingOf(civ), id] } }, [`${COUNSELORS[id].name}, ${COUNSELORS[id].job}, waits at the door of the Hall - a chair must be given up`]];
};

/** The Hall seats someone from the door into a free chair. Returns civ. */
export const seatFromDoor = (civ, id, round) => {
  const hall = civ.hall;
  if (!waitingOf(civ).includes(id) || councilOf(civ).length >= COUNCIL_SEATS) return civ;
  return { ...civ, hall: { ...hall, waiting: waitingOf(civ).filter((x) => x !== id), council: [...councilOf(civ), { id, since: round }] } };
};

/** Seat any founder this game is owed. Returns civ (unchanged if none). */
export const seatFounders = (civ, round) => {
  const hall = civ?.hall;
  if (!hall) return civ;
  const council = [...(hall.council || [])];
  const waiting = hall.waiting || [];
  const left = hall.left || {};
  const before = council.length;
  FOUNDERS.forEach((id) => {
    if (council.length >= COUNCIL_SEATS) return;
    if (council.some((c) => c.id === id) || waiting.includes(id)) return;
    if (left[id] !== undefined) return;
    council.push({ id, since: round });
  });
  if (council.length === before) return civ;
  return { ...civ, hall: { ...hall, council } };
};

/** The Hall thanks a counselor and shows them out. Returns civ. */
export const dismiss = (civ, id, round) => {
  const hall = civ.hall;
  if (!seated(civ, id)) return civ;
  return { ...civ, hall: { ...hall, council: councilOf(civ).filter((c) => c.id !== id), left: { ...(hall.left || {}), [id]: round } } };
};

